import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ResourceType } from '@/enums/ResourceType';
import { apiClient, ICollectionResponse } from '@/resources/upgrade/jsonapi-client';
import { enrichNotification, INotification } from '@/resources/upgrade/Notification';

// Root query key — invalidate this prefix after marking anything read to refresh
// every badge/count/list in one shot.
export const NOTIFICATIONS_QUERY_KEY = 'notifications';

// The header "bell" now aggregates lecture + community activity (but NOT chat,
// which has its own People popover keyed on EventChatChannel).
export const BELL_RESOURCE_TYPES = [
  ResourceType.Lecture,
  ResourceType.Comment,
  ResourceType.ThumbsUpReaction,
  ResourceType.CommunityPost,
];

type ResourceTypeFilter = ResourceType | Array<ResourceType>;

// Backend accepts a comma-joined list for multi-value resource_type filtering
// (confirmed), e.g. `filter[resource_type]=Lecture,Comment,Reactions::ThumbsUp`.
function toResourceTypeParam(resourceType?: ResourceTypeFilter): string | undefined {
  if (!resourceType) return undefined;

  return Array.isArray(resourceType) ? resourceType.join(',') : resourceType;
}

function resourceTypeKey(resourceType?: ResourceTypeFilter): string | null {
  return toResourceTypeParam(resourceType) ?? null;
}

export interface IUnreadNotifications {
  items: Array<INotification>;
  totalUnreadCount: number;
}

// Unread lookup used by every badge (bell, chat, inline) and by the chat
// components to detect/revalidate unread state. Keeps the payload tiny via
// fields[notifications]=id; the true total comes from root meta.
export function unreadNotificationsQueryOptions(
  eventId: string | undefined,
  resourceType?: ResourceTypeFilter,
  extraFilters?: Record<string, string>,
  refetchInterval?: number
) {
  return queryOptions({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'unread', eventId ?? null, resourceTypeKey(resourceType), extraFilters ?? null],
    queryFn: async (): Promise<IUnreadNotifications> => {
      const params: Record<string, string> = {
        'filter[event_id]': eventId as string,
        'filter[scope]': 'unread',
        'fields[notifications]': 'id',
        ...(toResourceTypeParam(resourceType) && {
          'filter[resource_type]': toResourceTypeParam(resourceType) as string,
        }),
        ...extraFilters,
      };

      const response = await apiClient.get<ICollectionResponse<INotification>>('notifications', params);

      return {
        items: response.data ?? [],
        totalUnreadCount: Number(response.meta?.total_unread_count ?? 0),
      };
    },
    enabled: Boolean(eventId),
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

// Unread notifications for a single chat channel — the chat inputs use this to
// find the notification id to mark read when the channel is focused. Mirrors the
// old datx queryChatChannelNotification (no event_id filter, by design).
export function channelUnreadNotificationsQueryOptions(channelId: string | undefined) {
  return queryOptions({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'unread', 'channel', channelId ?? null],
    queryFn: async () => {
      const response = await apiClient.get<ICollectionResponse<INotification>>('notifications', {
        'filter[scope]': 'unread',
        'filter[resource_type]': ResourceType.EventChatChannel,
        'filter[event_chat_channel_id]': channelId as string,
      });

      return response.data ?? [];
    },
    enabled: Boolean(channelId),
  });
}

// Per-channel unread counts for the direct-messaging channel list. Fetches the
// event's unread EventChatChannel notifications once and maps resource_id (the
// channel id) -> the notification's unread_count. Replaces the old datx
// `channel.notification.resourceMeta.unreadCount` relationship read.
export function eventChatChannelUnreadCountsQueryOptions(eventId: string | undefined, refetchInterval?: number) {
  return queryOptions({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'unread', 'channel-counts', eventId ?? null],
    queryFn: async (): Promise<Record<string, number>> => {
      const response = await apiClient.get<ICollectionResponse<INotification>>('notifications', {
        'filter[event_id]': eventId as string,
        'filter[scope]': 'unread',
        'filter[resource_type]': ResourceType.EventChatChannel,
      });

      const counts: Record<string, number> = {};

      for (const notification of response.data ?? []) {
        // first unread notification per channel wins, matching the old datx notifications[0]
        if (notification.resource_id && counts[notification.resource_id] === undefined) {
          counts[notification.resource_id] = Number(notification.meta?.unread_count ?? 0);
        }
      }

      return counts;
    },
    enabled: Boolean(eventId),
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

// The bell list. Cursor pagination (page[type]=cursor / page[after]=<last id>),
// matching the old datx fetcher. Includes the relationships each row renders.
export function notificationsInfiniteQueryOptions(
  eventId: string | undefined,
  resourceType: ResourceTypeFilter = BELL_RESOURCE_TYPES,
  pageSize = 10
) {
  return infiniteQueryOptions({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'list', eventId ?? null, resourceTypeKey(resourceType), pageSize],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = {
        'filter[event_id]': eventId as string,
        // `comment` links reaction (liked) notifications to the comment/reply
        // they're about, so the post page can scroll to and flash it.
        include: 'lecture,community_post,actor_profile,comment',
        'page[type]': 'cursor',
        'page[size]': String(pageSize),
        ...(toResourceTypeParam(resourceType) && {
          'filter[resource_type]': toResourceTypeParam(resourceType) as string,
        }),
        ...(pageParam ? { 'page[after]': pageParam } : {}),
      };

      const response = await apiClient.get<ICollectionResponse<INotification>>('notifications', params);
      const items = response.data.map(enrichNotification);
      const hasNext = Boolean(response.links?.next);

      return {
        items,
        nextCursor: hasNext && items.length ? items[items.length - 1].id : undefined,
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: Boolean(eventId),
  });
}

// PATCH /notifications/:id — marks a notification read. Callers invalidate the
// NOTIFICATIONS_QUERY_KEY prefix afterwards to refresh badges/lists.
export async function markNotificationRead(id: string): Promise<void> {
  await apiClient.patch(`notifications/${id}`, {
    data: {
      type: 'notifications',
      id,
      attributes: { read: true },
    },
  });
}
